import { IFlatMoTreeNode } from '@interfaces/components/tree';

import { FLAT_NODE_PATH_SEPARATOR } from '@components/common/FlatTree/constants';

const initNodesByDepth = (
	nodes: IFlatMoTreeNode[],
): { [depth: number]: IFlatMoTreeNode[] } => {
	return nodes.reduce<{ [depth: number]: IFlatMoTreeNode[] }>((acc, node) => {
		if (!acc[node.depth]) {
			acc[node.depth] = [];
		}

		acc[node.depth].push(node);

		return acc;
	}, {});
};

export const sortNodesByCheckedCount = (nodes: IFlatMoTreeNode[]): IFlatMoTreeNode[] => {
	const nodesByDepth: { [depth: number]: IFlatMoTreeNode[] } = initNodesByDepth(nodes);
	const sortedNodes: IFlatMoTreeNode[] = [];

	Object.keys(nodesByDepth).forEach((depth: any) => {
		nodesByDepth[depth].sort((a, b) => {
			if (a.isChecked && a.countOfChildren === 0 && !(b.isChecked && b.countOfChildren === 0)) {
				return -1;
			}
			if (b.isChecked && b.countOfChildren === 0 && !(a.isChecked && a.countOfChildren === 0)) {
				return 1;
			}

			if (b.countOfChecked === 0 && a.countOfChecked !== 0) {
				return -1;
			}

			if (a.countOfChecked === 0 && b.countOfChecked !== 0) {
				return 1;
			}

			if (a.countOfChecked !== 0 && b.countOfChecked !== 0) {
				return a.key.localeCompare(b.key);
			}

			if (!a.isChecked && !b.isChecked) {
				return a.key.localeCompare(b.key);
			}

			if (a.isChecked && b.isChecked) {
				return a.key.localeCompare(b.key);
			}

			return 0;
		});
	});

	addNodesAtDepth(0, null, nodesByDepth, sortedNodes);

	return sortedNodes;
};

export const sortNodesAlphabetically = (nodes: IFlatMoTreeNode[]): IFlatMoTreeNode[] => {
	const nodesByDepth: { [depth: number]: IFlatMoTreeNode[] } = initNodesByDepth(nodes);
	const sortedNodes: IFlatMoTreeNode[] = [];

	Object.keys(nodesByDepth).forEach((depth: any) => {
		nodesByDepth[depth].sort((a, b) => a.key.localeCompare(b.key));
	});

	addNodesAtDepth(0, null, nodesByDepth, sortedNodes);

	return sortedNodes;
};

const addNodesAtDepth = (
	depth: number,
	parentPath: string | null = null,
	nodesByDepth: { [depth: number]: IFlatMoTreeNode[] },
	sortedNodes: IFlatMoTreeNode[],
) => {
	if (!nodesByDepth[depth]) return;

	nodesByDepth[depth].forEach(node => {
		if (parentPath === null || node.path.startsWith(parentPath)) {
			sortedNodes.push(node);
			addNodesAtDepth(
				depth + 1,
				node.path,
				nodesByDepth,
				sortedNodes,
			);
		}
	});
};

export const filterNodesBySearchText = (nodes: IFlatMoTreeNode[]) => (searchText: string): IFlatMoTreeNode[] => {
	if (searchText.trim() === '') {
		return clearSearchText(nodes);
	}

	const nodesByPath: { [path: string]: IFlatMoTreeNode } = {};
	const visibleParentPaths = new Set<string>();
	const matchingNodes = new Set<string>();

	nodes.forEach(node => {
		const isMatch = node.key.toLowerCase().includes(searchText.toLowerCase());
		const isVisible = isMatch || visibleParentPaths.has(node.path);

		nodesByPath[node.path] = {
			...node,
			isHidden: !isVisible,
			isHiddenChildren: !isVisible,
			isCollapsed: false,
		};

		if (isMatch) {
			matchingNodes.add(node.path);
			nodesByPath[node.path] = {
				...node,
				isHidden: false,
				isHiddenChildren: true,
				isCollapsed: false,
			};
		}

		if (isVisible) {
			const pathParts = node.path.split(FLAT_NODE_PATH_SEPARATOR);
			for (let i = 1; i < pathParts.length; i++) {
				const parentPath = pathParts.slice(0, i).join(FLAT_NODE_PATH_SEPARATOR);

				visibleParentPaths.add(parentPath);

				if (nodesByPath[parentPath]) {
					nodesByPath[parentPath] = {
						...nodesByPath[parentPath],
						isHidden: false,
						isHiddenChildren: false,
					};
				}
			}
		}
	});

	nodes.forEach(node => {
		const pathParts = node.path.split(FLAT_NODE_PATH_SEPARATOR);
		for (let i = 1; i < pathParts.length; i++) {
			const parentPath = pathParts.slice(0, i).join(FLAT_NODE_PATH_SEPARATOR);
			if (matchingNodes.has(parentPath)) {
				nodesByPath[node.path] = {
					...node,
					isHidden: false,
					isHiddenChildren: true,
					isCollapsed: true,
				};
			}
		}
	});

	return Object.values(nodesByPath);
};

export const prepareKpiIdsData = (data: IFlatMoTreeNode[]): string[] => {
	return data.reduce<string[]>((acc, node) => {
		return node.isChecked && !!node.dbId ? [...acc, node.dbId] : acc;
	}, []);
};

export const clearSearchText = (nodes: IFlatMoTreeNode[]): IFlatMoTreeNode[] => {
	return nodes.map(node => ({
		...node,
		isCollapsed: false,
		isHiddenChildren: false,
		isHidden: false,
	}));
};

export const getCountOfCheckedParameters = (nodes: IFlatMoTreeNode[]): number => {
	return nodes.reduce((acc, node) => {
		return acc + (node.isChecked && !node.nodeType ? 1 : 0);
	}, 0);
};

export const getCountOfCheckedNodes = (nodes: IFlatMoTreeNode[]): number => {
	return nodes.reduce((acc, node) => {
		const counter = node.depth === 0 ? node.countOfChecked : 0;
		const isParentChecked = node.isChecked && counter > 0;

		return acc + counter + (isParentChecked ? 1 : 0);
	}, 0);
};
